import { HttpMethods, makeRequest } from '../helpers/http.helper';
import type {
  CollectionFlow,
  FlowContract,
  FlowStepInput,
  FlowStepType,
  FlowType,
  ResolvedFlow,
} from '../types/collection-flow.type';

interface GenericResponse<T> {
  result: T;
}

/** The catalogue is global reference data; every org reads the same rows. */
export async function listFlowStepTypes(organizationId: string): Promise<FlowStepType[]> {
  const response = (await makeRequest({
    url: '/ops/collection-flows/step-types',
    method: HttpMethods.GET,
    params: { organization_id: organizationId },
  })) as GenericResponse<FlowStepType[]>;
  return response.result;
}

export async function getFlowContract(
  organizationId: string,
  flowType: FlowType,
  facilityId?: string
): Promise<FlowContract> {
  const response = (await makeRequest({
    url: '/ops/collection-flows/contract',
    method: HttpMethods.GET,
    params: { organization_id: organizationId, flow_type: flowType, facility_id: facilityId },
  })) as GenericResponse<FlowContract>;
  return response.result;
}

export async function listFlows(
  organizationId: string,
  params: { supplier_id?: string; facility_id?: string; include_superseded?: boolean }
): Promise<CollectionFlow[]> {
  const response = (await makeRequest({
    url: '/ops/collection-flows',
    method: HttpMethods.GET,
    params: { organization_id: organizationId, ...params },
  })) as GenericResponse<CollectionFlow[]>;
  return response.result;
}

export async function getActiveFlow(
  organizationId: string,
  flowType: FlowType,
  subject: { supplier_id?: string; facility_id?: string }
): Promise<ResolvedFlow> {
  const response = (await makeRequest({
    url: '/ops/collection-flows/active',
    method: HttpMethods.GET,
    params: { organization_id: organizationId, flow_type: flowType, ...subject },
  })) as GenericResponse<ResolvedFlow>;
  return response.result;
}

export async function saveFlowDraft(
  organizationId: string,
  body: {
    flow_type: FlowType;
    supplier_id?: string;
    facility_id?: string;
    name?: string;
    steps: FlowStepInput[];
  }
): Promise<CollectionFlow> {
  const response = (await makeRequest({
    url: '/ops/collection-flows/draft',
    method: HttpMethods.PUT,
    params: { organization_id: organizationId },
    data: body,
  })) as GenericResponse<CollectionFlow>;
  return response.result;
}

/** Contract-checked server-side: a flow that would strand a driver is rejected here. */
export async function activateFlow(organizationId: string, flowId: string): Promise<CollectionFlow> {
  const response = (await makeRequest({
    url: `/ops/collection-flows/${flowId}/activate`,
    method: HttpMethods.POST,
    params: { organization_id: organizationId },
  })) as GenericResponse<CollectionFlow>;
  return response.result;
}
